import { FileDoneOutlined, UploadOutlined } from "@ant-design/icons"
import { Dropdown, type MenuProps } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../components/ActionTooltip.tsx"

export type AddInvoiceKind = "generated" | "external"

interface AddInvoiceMenuProps {
  generateAllowed?: boolean
  externalAllowed?: boolean
  disabled?: boolean
  /** Tooltip for the trigger; it has to sit outside the menu to reach it. */
  tooltip?: string
  onSelect: (kind: AddInvoiceKind) => void
  /** The control that opens the menu. It receives the click itself. */
  children: React.ReactElement
}

/**
 * A drawer or modal is its own stacking context, so a menu parked on
 * `document.body` would open under the overlay it was clicked in.
 */
export const addInvoiceMenuContainer = (trigger: HTMLElement): HTMLElement =>
  (trigger.closest(".ant-drawer-body, .ant-modal-body") as HTMLElement | null) ??
  document.body

/**
 * The one way every screen adds an invoice: generate it in EZBuild, or record
 * one made elsewhere. With a single allowed option the choice is already made,
 * so the control opens that drawer directly rather than a menu of one.
 */
export function AddInvoiceMenu({
  generateAllowed = false,
  externalAllowed = false,
  disabled = false,
  tooltip,
  onSelect,
  children
}: AddInvoiceMenuProps) {
  const { t } = useTranslation()
  // antd's own click-to-select-and-close only fires reliably when `open` is
  // ours to clear; left uncontrolled, selecting an item picked up the drawer
  // it just opened (getPopupContainer moves the popup into that drawer's own
  // body) but left the menu itself mounted and floating over it.
  const [open, setOpen] = useState(false)

  const kinds: AddInvoiceKind[] = [
    ...(generateAllowed ? (["generated"] as const) : []),
    ...(externalAllowed ? (["external"] as const) : [])
  ]

  if (!kinds.length) return null

  const withTooltip = (trigger: React.ReactElement) =>
    tooltip ? <ActionTooltip title={tooltip}>{trigger}</ActionTooltip> : trigger

  if (kinds.length === 1) {
    const [only] = kinds
    return withTooltip(
      React.cloneElement(children as React.ReactElement<{
        onClick?: React.MouseEventHandler
      }>, {
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation()
          if (!disabled) onSelect(only)
        }
      })
    )
  }

  const items: MenuProps["items"] = [
    {
      key: "generated",
      icon: <FileDoneOutlined />,
      label: t("invoices.addMenu.generate")
    },
    {
      key: "external",
      icon: <UploadOutlined />,
      label: t("invoices.addMenu.external")
    }
  ]

  return withTooltip(
    <Dropdown
      trigger={disabled ? [] : ["click"]}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      // Without focus in the menu, a keyboard user opens it and cannot move.
      autoFocus
      getPopupContainer={addInvoiceMenuContainer}
      menu={{
        items,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          setOpen(false)
          if (key === items[0]?.key) {
            // Deferred past this tick, and only for this one item: it's the
            // one `autoFocus` lands on when the menu opens, which is also the
            // one a mouse click races against rc-menu's own mousedown-driven
            // blur/close handling — a click there closed the menu but
            // silently dropped the selection. Every other item never had
            // this race and stays synchronous, since several existing call
            // sites depend on that for the same tick's assertions.
            setTimeout(() => onSelect(key as AddInvoiceKind), 0)
            return
          }
          onSelect(key as AddInvoiceKind)
        }
      }}
    >
      {children}
    </Dropdown>
  )
}
